import { type FC, useState } from 'react';
import styled from 'styled-components';

import { AssetFilterFields } from '@/_pages/assets/_pageModule/components/AssetFilters/AssetFilterFields';
import {
  MobileSortOrder,
  useMobileAssetSortStore,
} from '@/_pages/assets/_pageModule/stores/mobileAssetSort.store';
import { usePageAssetsFilterStore } from '@/_pages/assets/_pageModule/stores/pageAssetsFilter.store';
import { FilterValue } from '@/_pages/assets/_pageModule/types';
import { COLORS } from '@/shared/constants/colors';
import { Button } from '@/shared/uikit/Button';
import { Title } from '@/shared/uikit/Title';
import { Radio } from '@/shared/uikitV2/Radio';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { ModalSlideContainer } from '@/widgets/features/ModalSlideContainer';

import { useModalsStore } from '../..';
import { MobileAssetFiltersProps } from '../../types';

export const MobileAssetFiltersModal: FC<MobileAssetFiltersProps> = ({ filterOptions }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'mobileAssetFiltersModal' });
  const modals = useModalsStore();

  const {
    setMany,
    reset,
    search: searchStore,
    category: categoryStore,
    country: countryStore,
    currency: currencyStore,
    valueFrom: valueFromStore,
    valueTo: valueToStore,
  } = usePageAssetsFilterStore();
  const { mobileSort, setMobileSort, resetMobileSort } = useMobileAssetSortStore();

  const [localSort, setLocalSort] = useState<MobileSortOrder | null>(mobileSort || 'oldToNew');
  const [search, setSearch] = useState<FilterValue['search']>(searchStore);
  const [category, setCategory] = useState<FilterValue['category']>(categoryStore);
  const [country, setCountry] = useState<FilterValue['country']>(countryStore);

  const [currency, setCurrency] = useState<FilterValue['currency']>(currencyStore);
  const [valueFrom, setValueFrom] = useState<FilterValue['valueFrom']>(valueFromStore);
  const [valueTo, setValueTo] = useState<FilterValue['valueTo']>(valueToStore);

  const filterValues: FilterValue = { search, category, country, currency, valueFrom, valueTo };
  const someFilled = Boolean(
    search || category || country || currency || valueFrom || valueTo || mobileSort,
  );

  const resetAll = () => {
    setSearch('');
    setCategory(null);
    setCountry(null);
    setCurrency(null);
    setValueFrom(null);
    setValueTo(null);
    reset();
    resetMobileSort();
    modals.closeModal();
  };

  const resetOneOf = (key: keyof FilterValue) => {
    const resetMap: { [K in keyof FilterValue]: () => void } = {
      search: () => setSearch(''),
      category: () => setCategory(null),
      country: () => setCountry(null),
      currency: () => setCurrency(null),
      valueFrom: () => setValueFrom(null),
      valueTo: () => setValueTo(null),
    };
    resetMap[key]();
  };

  const handleApply = () => {
    setMany(filterValues);
    setMobileSort(localSort);
    modals.closeModal();
  };

  return (
    <StyledMobileAssetFiltersModal
      title={t('title')}
      footer={null}
      open
      onCancel={modals.closeModal}
      width={600}
      drawerHeight="83vh"
      drawerStyles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}
    >
      <AssetSortBox>
        <Title bold size={14}>
          {t('subSortTitle')}
        </Title>
        <Sorts>
          {(['oldToNew', 'newToOld', 'alphaAZ', 'alphaZA'] as const).map((option) => (
            <Radio
              key={option}
              checked={localSort === option}
              onChange={() => setLocalSort(option)}
            >
              {t(`sortOptions.${option}`)}
            </Radio>
          ))}
        </Sorts>
      </AssetSortBox>
      <AssetFilterBox>
        <Title bold size={14}>
          {t('subFilterTitle')}
        </Title>
        <AssetFilterFields
          hideSearch
          filterOptions={filterOptions}
          {...filterValues}
          setSearch={setSearch}
          setCategory={setCategory}
          setCountry={setCountry}
          setCurrency={setCurrency}
          setValueFrom={setValueFrom}
          setValueTo={setValueTo}
          resetOneOf={resetOneOf}
        />
      </AssetFilterBox>
      <Buttons>
        <Button disabled={!someFilled} style={{ height: 44 }} type="default" onClick={resetAll}>
          {t('reset')}
        </Button>

        <Button onClick={handleApply} style={{ height: 44 }}>
          {t('apply')}
        </Button>
      </Buttons>
    </StyledMobileAssetFiltersModal>
  );
};

const AssetFilterBox = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding-inline: 16px;
  padding-top: 12px;

  & > *:nth-child(-n + 4) {
    grid-column: span 2;
  }
`;

const AssetSortBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-inline: 16px;
  padding-top: 12px;
`;

const Sorts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledMobileAssetFiltersModal = styled(ModalSlideContainer)`
  &&& .ant-drawer-title h1 {
    width: 100%;
  }
`;

const Buttons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  width: 100%;
  padding-inline: 16px;
  padding-top: 12px;
  padding-bottom: 32px;
  gap: 12px;
  border-top: 1px solid ${COLORS.systemDark100};
`;
